import { newIdentifier } from '@cadet-dsl';
import { ADAPTIVE_BLUE } from '@color';
import type { Point } from '@geom';
import { EdgeDirection, type EdgeEndpoint, type EID, type NID } from '@graph';
import type { VisualCache } from '@visual';
import type { Doc } from './Doc';
import { EdgeSpec } from './EdgeSpec';
import { GroupSpec } from './GroupSpec';
import { layoutNode } from './Layouter';
import { UnitSpec } from './UnitSpec';
import { iString } from '@strings';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/**
 * Creates a new Unit or Group node and (optionally) attaches it to a parent.
 * Returns the new node ID.
 */
export function createNodeMutation(
  doc: Doc,
  cls: string | undefined,
  isGroup: boolean,
  cache: VisualCache,
  docPosition?: Point,
  parent?: NID
): NID {
  const nodeId: NID = newIdentifier('n', doc.graph.nodes);
  const newClass = cls ?? 'Unit';

  const title = iString(doc.firstAvailableLetterCode());
  const spec = isGroup
    ? new GroupSpec(title, ADAPTIVE_BLUE)
    : new UnitSpec(doc, newClass, { title });

  if (parent) {
    const parentEdge: EID = newIdentifier();
    doc.graph.addNode(nodeId, { data: spec, parent, newEdge: parentEdge });
  } else {
    doc.graph.addNode(nodeId, { data: spec });
  }

  layoutNode(doc, nodeId, cache, docPosition);
  return nodeId;
}

export function setNodeClassMutation(
  doc: Doc,
  node: NID,
  cls: string | undefined,
  cache: VisualCache
): boolean {
  const newClass = cls ?? 'Unit';
  if (doc.nodeClass(node) === newClass) {
    return false;
  }
  const isUnit = doc.isUnit(node);
  if (isUnit !== doc.classes.isUnitClass(newClass)) {
    return false;
  }
  doc.setNodeClass(node, newClass);
  layoutNode(doc, node, cache);
  return true;
}

/**
 * Creates a new edge between source and target nodes.
 * Returns the new edge ID.
 */
export function createEdgeMutation(doc: Doc, source: NID, target: NID): EID {
  const edgeId: EID = newIdentifier('e', doc.graph.edges);
  doc.graph.addEdge(edgeId, source, target, new EdgeSpec('->', {}));
  return edgeId;
}

/**
 * Inserts a unit in the middle of an existing edge.
 * Returns `{ newNode, newEdge }`.
 */
export function insertUnitMutation(
  doc: Doc,
  cls: string | undefined,
  docPosition: Point | undefined,
  edge: EID,
  newNodeParent: NID | undefined,
  cache: VisualCache
): { newNode: NID; newEdge: EID } {
  const target = doc.graph.target(edge);
  const newNode = createNodeMutation(doc, cls, false, cache, docPosition, newNodeParent);
  doc.graph.moveEdge(edge, { newTarget: newNode });

  const newEdge: EID = newIdentifier('e', doc.graph.edges);

  doc.graph.addEdge(newEdge, newNode, target, new EdgeSpec('->', {}));
  return { newNode, newEdge };
}

export function moveEdgeMutation(
  doc: Doc,
  edge: EID,
  newSource: NID,
  newTarget: NID
): void {
  // Prevent connections to groups
  if (doc.isGroup(newSource)) {
    throw new Error('moveEdgeMutation: newSource is a group');
  }
  if (doc.isGroup(newTarget)) {
    throw new Error('moveEdgeMutation: newTarget is a group');
  }

  doc.graph.moveEdge(edge, { newSource, newTarget });
}

export function redirectEdgeMutation(
  doc: Doc,
  edgeEndpoint: EdgeEndpoint,
  destination: NID
): void {
  const { edge, direction } = edgeEndpoint;
  let newSource: NID;
  let newTarget: NID;
  switch (direction) {
    case EdgeDirection.TAIL:
      newSource = destination;
      newTarget = doc.graph.target(edge);
      break;
    case EdgeDirection.HEAD:
      newSource = doc.graph.source(edge);
      newTarget = destination;
      break;
    default:
      throw new Error(`Unknown direction ${direction as string}`);
  }
  moveEdgeMutation(doc, edge, newSource, newTarget);
}

export function reverseEdgeMutation(doc: Doc, edge: EID): void {
  const source = doc.graph.source(edge);
  const target = doc.graph.target(edge);
  moveEdgeMutation(doc, edge, target, source);
}
